import { motion } from "framer-motion";
import { MessageCircle, ArrowDown, Sparkles } from "lucide-react";
import { waLink } from "@/lib/api";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.45 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export function Hero({ content }) {
  const video = content.videos?.[0];
  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-emerald-500/12 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-yellow-600/10 blur-[140px]" />
        <svg className="absolute top-1/2 left-0 w-full opacity-30" viewBox="0 0 1200 120" fill="none" preserveAspectRatio="none">
          <path className="heartbeat-line" d="M0 60 H420 L450 20 L480 100 L510 40 L530 60 H760 L790 15 L820 105 L850 45 L870 60 H1200" stroke="#D4AF37" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.2fr_0.8fr] gap-14 items-center w-full">
        <div>
          <motion.div
            initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-40 h-40 sm:w-52 sm:h-52 mb-8"
            data-testid="hero-logo"
          >
            <span className="absolute inset-0 rounded-[2.5rem] border border-emerald-500/40 pulse-ring" />
            <span className="absolute inset-0 rounded-[2.5rem] border border-yellow-600/30 pulse-ring" style={{ animationDelay: "1.2s" }} />
            <img src={content.logoUrl} alt={content.businessName} className="w-full h-full rounded-[2.5rem] object-cover glow-emerald animate-float-slow" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 rounded-full px-4 py-1.5 mb-6"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5 text-yellow-500" />
            <span className="text-xs uppercase tracking-[0.22em] text-emerald-400 font-medium">{content.heroBadge}</span>
          </motion.div>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight leading-[0.95]">
            <span className="mask-line"><motion.span className="block" custom={0} variants={lineReveal} initial="hidden" animate="show">{content.heroLine1}</motion.span></span>
            <span className="mask-line"><motion.span className="block text-emerald-gradient" custom={1} variants={lineReveal} initial="hidden" animate="show">{content.heroLine2}</motion.span></span>
            <span className="mask-line"><motion.span className="block text-gold-gradient" custom={2} variants={lineReveal} initial="hidden" animate="show">{content.heroLine3}</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-gray-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {content.heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href="#productos" data-testid="hero-products-cta" className="group bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-7 py-3.5 rounded-full transition-colors duration-200 flex items-center gap-2">
              Ver Suplementos
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-200" />
            </a>
            <a
              href={waLink(content.whatsapp, "Hola, quiero información sobre los suplementos 365 Cellular Energy.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="border border-yellow-600/50 text-yellow-500 hover:bg-yellow-600/10 font-semibold px-7 py-3.5 rounded-full transition-colors duration-200 flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Contactar por WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-12 grid grid-cols-3 max-w-md gap-6"
            data-testid="hero-stats"
          >
            {(content.stats || []).map((s, i) => (
              <div key={i}>
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-emerald-gradient">{s.value}</div>
                <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {video && (
          <motion.div
            initial={{ opacity: 0, x: 60, rotate: 4 }}
            animate={{ opacity: 1, x: 0, rotate: 2 }}
            transition={{ delay: 0.8, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block justify-self-end"
            data-testid="hero-video-card"
          >
            <div className="relative w-72 rounded-[2rem] overflow-hidden border border-emerald-500/25 glow-emerald">
              <video src={video.url} poster={video.url.replace(".mp4", ".jpg")} autoPlay muted loop playsInline preload="metadata" className="w-full aspect-[9/16] object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-5">
                <span className="text-[10px] uppercase tracking-[0.25em] text-yellow-500 font-medium">{video.caption}</span>
                <p className="text-sm font-semibold text-white mt-1">{video.title}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
